import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LangProvider } from '../../hooks/useLang';
import { useLang } from '../../hooks/useLang';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import RiskModal from '../home/components/RiskModal';
import CommentSection from './components/CommentSection';
import RewardModal from './components/RewardModal';
import SubscribeModal from './components/SubscribeModal';
import { columnArticles } from '../../mocks/column';
import { columnContents } from '../../mocks/columnContent';

const categoryColors: Record<string, string> = {
  '政策深析': 'bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20',
  '合规实务': 'bg-[#059669]/10 text-[#059669] border-[#059669]/20',
  '实操指南': 'bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20',
  '风险警示': 'bg-[#DC2626]/10 text-[#DC2626] border-[#DC2626]/20',
  '案例解析': 'bg-[#D97706]/10 text-[#D97706] border-[#D97706]/20',
  '行业动态': 'bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20',
};

const ARTICLE_LABELS: Record<string, Record<string, string>> = {
  home: { zh: '首页', en: 'Home', fr: 'Accueil', es: 'Inicio', pt: 'Início', ja: 'ホーム', de: 'Startseite', ko: '홈', ar: 'الرئيسية', ru: 'Главная' },
  column: { zh: '清鉴纵论', en: 'ChaInsight Column', fr: 'Colonne ChaInsight', es: 'Columna ChaInsight', pt: 'Coluna ChaInsight', ja: '清鑑縦論', de: 'ChaInsight Kolumne', ko: 'ChaInsight 칼럼', ar: 'عمود ChaInsight', ru: 'Колонка ChaInsight' },
  notFound: { zh: '文章不存在', en: 'Article not found', fr: 'Article introuvable', es: 'Artículo no encontrado', pt: 'Artigo não encontrado', ja: '記事が見つかりません', de: 'Artikel nicht gefunden', ko: '기사를 찾을 수 없습니다', ar: 'المقال غير موجود', ru: 'Статья не найдена' },
  backToColumn: { zh: '返回专栏', en: 'Back to Column', fr: 'Retour à la colonne', es: 'Volver a la columna', pt: 'Voltar à coluna', ja: 'コラムに戻る', de: 'Zurück zur Kolumne', ko: '칼럼으로 돌아가기', ar: 'العودة إلى العمود', ru: 'Назад к колонке' },
  featured: { zh: '精选', en: 'Featured', fr: 'À la une', es: 'Destacado', pt: 'Destaque', ja: '注目', de: 'Empfohlen', ko: '추천', ar: 'مميز', ru: 'Рекомендуемое' },
  readTime: { zh: '约{n}分钟阅读', en: '~{n} min read', fr: '~{n} min de lecture', es: '~{n} min de lectura', pt: '~{n} min de leitura', ja: '約{n}分で読める', de: '~{n} Min. Lesezeit', ko: '약 {n}분 읽기', ar: '~{n} دقيقة للقراءة', ru: '~{n} мин чтения' },
  views: { zh: '次阅读', en: 'views', fr: 'vues', es: 'vistas', pt: 'visualizações', ja: '回閲覧', de: 'Aufrufe', ko: '조회', ar: 'مشاهدات', ru: 'просмотров' },
  abstract: { zh: '摘要 · Abstract', en: 'Abstract', fr: 'Résumé', es: 'Resumen', pt: 'Resumo', ja: '要旨', de: 'Zusammenfassung', ko: '요약', ar: 'ملخص', ru: 'Аннотация' },
  closingVerse: { zh: '以词作结 · 词牌落款', en: 'Closing Verse', fr: 'Vers de clôture', es: 'Verso de cierre', pt: 'Verso de encerramento', ja: '締めくくりの詩', de: 'Abschlussvers', ko: '마무리 시', ar: 'مقطع الختام', ru: 'Заключительный стих' },
  loading: { zh: '文章内容加载中...', en: 'Loading article content...', fr: 'Chargement du contenu...', es: 'Cargando contenido...', pt: 'Carregando conteúdo...', ja: '記事コンテンツを読み込み中...', de: 'Inhalt wird geladen...', ko: '기사 내용 로딩 중...', ar: 'جارٍ تحميل المحتوى...', ru: 'Загрузка содержимого...' },
  writtenOn: { zh: '成文于', en: 'Written on', fr: 'Écrit le', es: 'Escrito el', pt: 'Escrito em', ja: '執筆日', de: 'Geschrieben am', ko: '작성일', ar: 'كُتب في', ru: 'Написано' },
  insightful: { zh: '有启发', en: 'Insightful', fr: 'Instructif', es: 'Perspicaz', pt: 'Perspicaz', ja: '参考になった', de: 'Aufschlussreich', ko: '유익함', ar: 'مفيد', ru: 'Полезно' },
  read: { zh: '已读', en: 'read', fr: 'lu', es: 'leído', pt: 'lido', ja: '既読', de: 'gelesen', ko: '읽음', ar: 'مقروء', ru: 'прочитано' },
  subscribe: { zh: '订阅', en: 'Subscribe', fr: 'S\'abonner', es: 'Suscribirse', pt: 'Assinar', ja: '購読', de: 'Abonnieren', ko: '구독', ar: 'اشترك', ru: 'Подписаться' },
  reward: { zh: '打赏支持', en: 'Reward', fr: 'Récompenser', es: 'Recompensar', pt: 'Recompensar', ja: '投げ銭', de: 'Belohnen', ko: '후원', ar: 'مكافأة', ru: 'Поддержать' },
  previous: { zh: '上一篇', en: 'Previous', fr: 'Précédent', es: 'Anterior', pt: 'Anterior', ja: '前の記事', de: 'Vorherige', ko: '이전 글', ar: 'السابق', ru: 'Предыдущая' },
  next: { zh: '下一篇', en: 'Next', fr: 'Suivant', es: 'Siguiente', pt: 'Próximo', ja: '次の記事', de: 'Nächste', ko: '다음 글', ar: 'التالي', ru: 'Следующая' },
  backToColumnBtn: { zh: '返回清鉴纵论', en: 'Back to ChaInsight Column', fr: 'Retour à la colonne', es: 'Volver a la columna', pt: 'Voltar à coluna', ja: 'コラムに戻る', de: 'Zurück zur Kolumne', ko: '칼럼으로 돌아가기', ar: 'العودة إلى العمود', ru: 'Назад к колонке' },
};

function ArticleDetailContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { lang } = useLang();
  const getLabel = (key: string) => ARTICLE_LABELS[key]?.[lang] ?? ARTICLE_LABELS[key]?.['en'] ?? key;
  const [riskModal, setRiskModal] = useState(false);
  const [rewardOpen, setRewardOpen] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [readProgress, setReadProgress] = useState(0);

  const article = columnArticles.find(a => a.id === id);
  const content = columnContents.find(c => c.id === id);
  const currentIndex = columnArticles.findIndex(a => a.id === id);
  const prevArticle = currentIndex > 0 ? columnArticles[currentIndex - 1] : null;
  const nextArticle = currentIndex < columnArticles.length - 1 ? columnArticles[currentIndex + 1] : null;

  useEffect(() => {
    if (article) setLikeCount(article.likeCount);
  }, [article]);

  useEffect(() => {
    const handleScroll = () => {
      const el = document.documentElement;
      const scrollTop = el.scrollTop || document.body.scrollTop;
      const scrollHeight = el.scrollHeight - el.clientHeight;
      setReadProgress(scrollHeight > 0 ? Math.min(100, Math.round((scrollTop / scrollHeight) * 100)) : 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">{getLabel('notFound')}</p>
          <button onClick={() => navigate('/column')} className="text-[#7C3AED] hover:underline cursor-pointer">{getLabel('backToColumn')}</button>
        </div>
      </div>
    );
  }

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    if (lang === 'zh' || lang === 'ja') {
      return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
    return d.toLocaleDateString(lang === 'ko' ? 'ko-KR' : lang === 'ar' ? 'ar' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleLike = () => {
    setLiked(!liked);
    setLikeCount(prev => liked ? prev - 1 : prev + 1);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Read Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-[99999] h-0.5 bg-gray-200">
        <div className="h-full bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] transition-all duration-100" style={{ width: `${readProgress}%` }}></div>
      </div>

      <Navbar onRiskModal={() => setRiskModal(true)} />
      <RiskModal open={riskModal} onClose={() => setRiskModal(false)} isFirstVisit={false} />
      <RewardModal open={rewardOpen} onClose={() => setRewardOpen(false)} articleTitle={article.title} />
      <SubscribeModal open={subscribeOpen} onClose={() => setSubscribeOpen(false)} />

      {/* Article Header */}
      <div className="bg-gradient-to-br from-[#1A0050] via-[#2E1065] to-[#1A0050] pt-24 pb-14 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/4 w-80 h-80 bg-[#7C3AED]/15 rounded-full blur-[100px]"></div>
          <div className="absolute top-0 right-1/3 w-56 h-56 bg-[#F59E0B]/8 rounded-full blur-[80px]"></div>
        </div>
        <div className="relative max-w-3xl mx-auto px-4 lg:px-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-white/40 text-xs mb-6">
            <button onClick={() => navigate('/')} className="hover:text-white/70 transition-colors cursor-pointer whitespace-nowrap">
              {getLabel('home')}
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <button onClick={() => navigate('/column')} className="hover:text-white/70 transition-colors cursor-pointer whitespace-nowrap">
              {getLabel('column')}
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/60 line-clamp-1">{article.title}</span>
          </div>

          <div className="flex items-center gap-3 mb-5">
            <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${categoryColors[article.category] || 'bg-gray-100 text-gray-600 border-gray-200'} bg-opacity-90`}>
              {article.category}
            </span>
            {article.featured && (
              <span className="text-xs bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/30 px-3 py-1 rounded-full font-semibold">
                {getLabel('featured')}
              </span>
            )}
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-white leading-tight mb-3">{article.title}</h1>
          <p className="text-white/55 text-base italic mb-6 leading-relaxed">{article.subtitle}</p>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-white/45 text-xs mb-6">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-[#A78BFA]"></i>
              {formatDate(article.publishedAt)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-[#A78BFA]"></i>
              {getLabel('readTime').replace('{n}', String(article.readTime))}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line text-[#A78BFA]"></i>
              {article.viewCount.toLocaleString()} {getLabel('views')}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {article.tags.map(tag => (
              <span key={tag} className="text-xs text-white/40 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">#{tag}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 lg:px-6 py-10">
        {/* Abstract */}
        <div className="bg-gradient-to-r from-[#7C3AED]/8 to-[#A78BFA]/5 border-l-4 border-[#7C3AED] rounded-r-xl p-6 mb-10">
          <p className="text-xs font-bold text-[#7C3AED] uppercase tracking-widest mb-2">{getLabel('abstract')}</p>
          <p className="text-gray-700 text-base leading-relaxed">{article.abstract}</p>
        </div>

        {/* Article Content */}
        {content ? (
          <article className="prose prose-lg max-w-none">
            {content.sections.map((section, i) => (
              <section key={i} className="mb-10">
                <h2 className="text-xl font-black text-gray-900 mb-5 pb-3 border-b border-gray-100 flex items-start gap-3">
                  <span className="text-[#7C3AED]/30 font-black text-2xl leading-none mt-0.5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                  <span className="leading-snug">{section.heading}</span>
                </h2>
                {section.sectionImage && (
                  <figure className="mb-6 rounded-xl overflow-hidden">
                    <img
                      src={section.sectionImage.src}
                      alt={section.sectionImage.caption}
                      className="w-full h-48 sm:h-56 object-cover object-top"
                    />
                    <figcaption className="text-center text-xs text-gray-400 py-2 px-3 bg-gray-50 italic">
                      {section.sectionImage.caption}
                    </figcaption>
                  </figure>
                )}
                {section.paragraphs.map((para, j) => (
                  <p key={j} className="text-gray-700 text-base leading-8 mb-5 first-letter:text-2xl first-letter:font-bold first-letter:text-[#7C3AED] first-letter:float-left first-letter:mr-1 first-letter:mt-1">
                    {para}
                  </p>
                ))}
              </section>
            ))}

            {/* Poem Section */}
            <div className="my-14 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#1A0050] to-[#2E1065] rounded-2xl"></div>
              <div className="absolute inset-0 opacity-10"
                style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '30px 30px' }}
              ></div>
              <div className="relative px-8 py-10 text-center">
                <div className="inline-flex items-center gap-2 bg-[#F59E0B]/15 border border-[#F59E0B]/25 text-[#F59E0B] text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
                  <i className="ri-quill-pen-line"></i>
                  {getLabel('closingVerse')}
                </div>
                <h3 className="text-[#F59E0B] text-2xl font-bold font-serif mb-6">{content.poem.title}</h3>
                <div className="text-white/85 text-base leading-9 whitespace-pre-line mb-8 font-serif max-w-lg mx-auto">{content.poem.lines}</div>
                <div className="w-32 h-px bg-white/20 mx-auto mb-6"></div>
                <p className="text-white/45 text-sm italic leading-relaxed max-w-lg mx-auto">{content.poem.annotation}</p>
              </div>
            </div>
          </article>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
            <i className="ri-article-line text-4xl text-gray-300 mb-4 block"></i>
            <p className="text-gray-500">{getLabel('loading')}</p>
          </div>
        )}

        {/* Signature */}
        <div className="mt-10 flex justify-end">
          <div className="text-right border-t border-dashed border-gray-200 pt-6">
            <p className="text-gray-400 text-sm mb-1">{getLabel('writtenOn')} {formatDate(article.publishedAt)}</p>
            <p className="text-gray-900 font-serif font-bold text-2xl tracking-widest" style={{ fontFamily: '"Ma Shan Zheng", "Noto Serif SC", serif' }}>
              清鉴元衡
            </p>
            <p className="text-gray-500 text-sm">先生</p>
            <p className="text-gray-400 text-xs mt-0.5 italic">Mr. ChaInsight</p>
          </div>
        </div>

        {/* Interaction Bar */}
        <div className="mt-12 sticky bottom-6 z-50">
          <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-xl shadow-gray-200/50 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleLike}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${liked ? 'bg-[#7C3AED] text-white shadow-md shadow-[#7C3AED]/25' : 'bg-gray-100 text-gray-700 hover:bg-[#7C3AED]/10 hover:text-[#7C3AED]'}`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={liked ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                </div>
                {getLabel('insightful')} · {likeCount}
              </button>
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-bar-chart-line text-[#7C3AED]"></i>
                </div>
                {readProgress}% {getLabel('read')}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSubscribeOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 hover:bg-[#7C3AED]/10 hover:text-[#7C3AED] rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-notification-line"></i>
                </div>
                {getLabel('subscribe')}
              </button>
              <button
                onClick={() => setRewardOpen(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] text-[#1A0050] rounded-xl text-sm font-black transition-all hover:opacity-90 cursor-pointer whitespace-nowrap shadow-md shadow-[#F59E0B]/25"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-award-line"></i>
                </div>
                {getLabel('reward')}
              </button>
            </div>
          </div>
        </div>

        {/* Comment Section */}
        <CommentSection articleId={article.id} commentCount={article.commentCount} />

        {/* Prev / Next Navigation */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
          {prevArticle && (
            <button
              onClick={() => { navigate(`/column/${prevArticle.id}`); window.scrollTo(0, 0); }}
              className="group flex items-start gap-4 bg-white border border-gray-100 hover:border-[#7C3AED]/30 rounded-2xl p-5 text-left transition-all hover:shadow-md cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center text-gray-400 group-hover:text-[#7C3AED] transition-colors mt-0.5 shrink-0">
                <i className="ri-arrow-left-line text-xl"></i>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-400 mb-1">{getLabel('previous')}</p>
                <p className="text-sm font-semibold text-gray-800 group-hover:text-[#7C3AED] transition-colors line-clamp-2">{prevArticle.title}</p>
              </div>
            </button>
          )}
          {nextArticle && (
            <button
              onClick={() => { navigate(`/column/${nextArticle.id}`); window.scrollTo(0, 0); }}
              className="group flex items-start gap-4 bg-white border border-gray-100 hover:border-[#7C3AED]/30 rounded-2xl p-5 text-right justify-end transition-all hover:shadow-md cursor-pointer"
            >
              <div className="min-w-0">
                <p className="text-xs text-gray-400 mb-1">{getLabel('next')}</p>
                <p className="text-sm font-semibold text-gray-800 group-hover:text-[#7C3AED] transition-colors line-clamp-2">{nextArticle.title}</p>
              </div>
              <div className="w-8 h-8 flex items-center justify-center text-gray-400 group-hover:text-[#7C3AED] transition-colors mt-0.5 shrink-0">
                <i className="ri-arrow-right-line text-xl"></i>
              </div>
            </button>
          )}
        </div>

        {/* Back to Column */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/column')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#2E1065] text-white font-bold text-sm rounded-full hover:bg-[#4C1D95] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            {getLabel('backToColumnBtn')}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default function ArticleDetailPage() {
  return (
    <LangProvider>
      <ArticleDetailContent />
    </LangProvider>
  );
}